import { useQuery } from "@tanstack/react-query";
import {
  AlertCircle,
  AlertTriangle,
  ArrowDownCircle,
  ArrowUpCircle,
  CheckCircle2,
  HelpCircle,
  Loader2,
  PiggyBank,
} from "lucide-react";
import { api } from "../lib/api";

interface Props {
  competenciaId: number;
}

// Mesmos status das cotas — MESMAS cores/ícones, para leitura idêntica.
interface ChamadaExtraLinha {
  unidade: string;
  status:
    | "EM_DIA"
    | "ANTECIPADO"
    | "ANTECIPACAO"
    | "ATRASO"
    | "INADIMPLENTE"
    | "INDETERMINADO";
  valor: string | null;
  data_pagamento: string | null;
  competencia_referente: string | null;
  competencia_paga_em: string | null;
  dias_atraso: number | null;
  fundo: string | null;
  observacao: string;
}

interface ChamadasExtrasResumo {
  regulares: number;
  antecipados: number;
  antecipacoes: number;
  em_atraso: number;
  inadimplentes: number;
  indeterminados: number;
  total_unidades: number;
  tem_fundo_reserva: boolean;
}

interface ChamadasExtrasResponse {
  competencia_id: number;
  periodo_str: string;
  vencimento_efetivo: string | null;
  linhas: ChamadaExtraLinha[];
  resumo: ChamadasExtrasResumo;
}

function fmtBRL(v: string | null | undefined): string {
  if (v == null) return "—";
  const n = Number(v);
  if (Number.isNaN(n)) return v;
  return n.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

function fmtDataBR(iso: string | null): string {
  if (!iso) return "—";
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
}

// Idêntico ao STATUS_CONFIG das cotas — mesmas cores para não confundir o síndico.
const STATUS_CONFIG: Record<
  ChamadaExtraLinha["status"],
  { label: string; classes: string; badge: string; icone: React.ReactNode }
> = {
  EM_DIA: {
    label: "Em dia",
    classes: "bg-emerald-50 hover:bg-emerald-100",
    badge: "bg-emerald-100 text-emerald-800 border-emerald-200",
    icone: <CheckCircle2 size={14} className="text-emerald-600" />,
  },
  ANTECIPADO: {
    label: "Antecipado",
    classes: "bg-sky-50 hover:bg-sky-100",
    badge: "bg-sky-100 text-sky-800 border-sky-200",
    icone: <ArrowDownCircle size={14} className="text-sky-600" />,
  },
  ANTECIPACAO: {
    label: "Antecipação",
    classes: "bg-indigo-50 hover:bg-indigo-100",
    badge: "bg-indigo-100 text-indigo-800 border-indigo-200",
    icone: <ArrowUpCircle size={14} className="text-indigo-600" />,
  },
  ATRASO: {
    label: "Atraso",
    classes: "bg-amber-50 hover:bg-amber-100",
    badge: "bg-amber-100 text-amber-800 border-amber-200",
    icone: <AlertTriangle size={14} className="text-amber-600" />,
  },
  INADIMPLENTE: {
    label: "Inadimplente",
    classes: "bg-rose-50 hover:bg-rose-100",
    badge: "bg-rose-100 text-rose-800 border-rose-200",
    icone: <AlertCircle size={14} className="text-rose-600" />,
  },
  INDETERMINADO: {
    label: "Indeterminado",
    classes: "bg-slate-50 hover:bg-slate-100",
    badge: "bg-slate-100 text-slate-700 border-slate-200",
    icone: <HelpCircle size={14} className="text-slate-500" />,
  },
};

const TITULO = "Chamadas Extras (Fundo de Reserva)";

function Wrapper({ children }: { children: React.ReactNode }) {
  return (
    <section className="mt-8">
      <div className="flex items-center gap-2 mb-3">
        <PiggyBank size={18} className="text-indigo-600" />
        <h3 className="text-lg font-semibold text-slate-900">{TITULO}</h3>
      </div>
      {children}
    </section>
  );
}

export function ChamadasExtrasSection({ competenciaId }: Props) {
  const { data, isLoading, error } = useQuery<ChamadasExtrasResponse>({
    queryKey: ["chamadas-extras", competenciaId],
    queryFn: () =>
      api.get<ChamadasExtrasResponse>(
        `/competencias/${competenciaId}/chamadas-extras`,
      ),
  });

  if (isLoading) {
    return (
      <Wrapper>
        <div className="flex items-center gap-2 text-slate-500 text-sm">
          <Loader2 size={16} className="animate-spin" />
          Carregando chamadas extras...
        </div>
      </Wrapper>
    );
  }

  if (error || !data) {
    return (
      <Wrapper>
        <div className="p-4 bg-slate-50 border border-slate-200 rounded-card text-sm text-slate-600">
          Não foi possível carregar as chamadas extras.
        </div>
      </Wrapper>
    );
  }

  // Empty state: mês sem chamada extra / sem Fundo de Reserva convocado.
  // NÃO é inadimplência — é o normal de meses sem aporte extraordinário.
  if (data.linhas.length === 0) {
    return (
      <Wrapper>
        <div className="p-4 bg-slate-50 border border-slate-200 rounded-card text-sm text-slate-600">
          Este mês não teve chamada extra (nenhum aporte ao Fundo de Reserva por
          unidade). Quando houver, cada apartamento aparece aqui — com os mesmos
          controles das cotas (em dia, antecipado, atraso e inadimplente).
        </div>
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      <div className="flex items-baseline justify-between mb-3 -mt-1">
        <span className="text-xs text-slate-500">
          Cada unidade contribui conforme a fração ideal — valores diferentes
          por apartamento.
        </span>
        {data.vencimento_efetivo && (
          <span className="text-xs text-slate-500">
            Vencimento: {fmtDataBR(data.vencimento_efetivo)}
          </span>
        )}
      </div>

      <div className="overflow-x-auto bg-white border border-slate-200 rounded-card">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wide">
            <tr>
              <th className="text-left px-4 py-2.5 font-medium">Unidade</th>
              <th className="text-left px-4 py-2.5 font-medium">Status</th>
              <th className="text-right px-4 py-2.5 font-medium">Valor</th>
              <th className="text-left px-4 py-2.5 font-medium">
                Data pagamento
              </th>
              <th className="text-left px-4 py-2.5 font-medium">Ref.</th>
              <th className="text-left px-4 py-2.5 font-medium">Fundo</th>
              <th className="text-left px-4 py-2.5 font-medium">Observação</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {data.linhas.map((l, idx) => {
              const cfg = STATUS_CONFIG[l.status];
              return (
                <tr
                  key={`${l.unidade}-${l.competencia_referente ?? "x"}-${idx}`}
                  className={cfg.classes}
                >
                  <td className="px-4 py-3 font-medium text-slate-900">
                    {l.unidade}
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-xs border ${cfg.badge}`}
                    >
                      {cfg.icone}
                      {cfg.label}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums">
                    {fmtBRL(l.valor)}
                  </td>
                  <td className="px-4 py-3 tabular-nums">
                    {fmtDataBR(l.data_pagamento)}
                  </td>
                  <td className="px-4 py-3 tabular-nums text-slate-600">
                    {l.competencia_referente ?? "—"}
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-500">
                    {l.fundo ?? "—"}
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-600">
                    {l.observacao}
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot className="bg-slate-50 border-t-2 border-slate-200">
            <tr>
              <td colSpan={7} className="px-4 py-3">
                <div className="flex flex-wrap gap-2 items-center text-xs">
                  <ChipResumo
                    valor={data.resumo.regulares}
                    label="Regulares"
                    cfg={STATUS_CONFIG.EM_DIA}
                  />
                  <ChipResumo
                    valor={data.resumo.antecipados}
                    label="Antecipados"
                    cfg={STATUS_CONFIG.ANTECIPADO}
                  />
                  {data.resumo.antecipacoes > 0 && (
                    <ChipResumo
                      valor={data.resumo.antecipacoes}
                      label="Antecipações p/ próximo mês"
                      cfg={STATUS_CONFIG.ANTECIPACAO}
                    />
                  )}
                  <ChipResumo
                    valor={data.resumo.em_atraso}
                    label="Em atraso"
                    cfg={STATUS_CONFIG.ATRASO}
                  />
                  <ChipResumo
                    valor={data.resumo.inadimplentes}
                    label="Inadimplentes"
                    cfg={STATUS_CONFIG.INADIMPLENTE}
                  />
                  {data.resumo.indeterminados > 0 && (
                    <ChipResumo
                      valor={data.resumo.indeterminados}
                      label="Indeterminados"
                      cfg={STATUS_CONFIG.INDETERMINADO}
                    />
                  )}
                  <span className="ml-auto text-slate-700">
                    <span className="font-semibold tabular-nums">
                      {data.resumo.total_unidades}
                    </span>{" "}
                    unidades no total
                  </span>
                </div>
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </Wrapper>
  );
}

function ChipResumo({
  valor,
  label,
  cfg,
}: {
  valor: number;
  label: string;
  cfg: (typeof STATUS_CONFIG)[ChamadaExtraLinha["status"]];
}) {
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md border ${cfg.badge}`}
    >
      {cfg.icone}
      <span className="font-semibold tabular-nums">{valor}</span>
      <span>{label}</span>
    </span>
  );
}
